import { useReducer } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, type User } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import { Feedback } from '../components/Feedback';
import { PasswordInput } from '../components/PasswordInput';
import { authLink, getReturnTo } from '../lib/authNavigation';

interface State {
  orgName: string;
  email: string;
  password: string;
  confirmPassword: string;
  tosAgreed: boolean;
  isLoading: boolean;
  errors: Record<string, string>;
}

type Field = 'orgName' | 'email' | 'password' | 'confirmPassword';

type Action =
  | { type: 'FIELD'; field: Field; value: string }
  | { type: 'TOS'; value: boolean }
  | { type: 'BLUR'; field: Field }
  | { type: 'SUBMIT' }
  | { type: 'ERROR'; field: string; message: string };

const initial: State = {
  orgName: '',
  email: '',
  password: '',
  confirmPassword: '',
  tosAgreed: false,
  isLoading: false,
  errors: {},
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateField(state: State, field: Field): string | null {
  switch (field) {
    case 'orgName':
      if (state.orgName.trim().length < 2) return 'organization name must be at least 2 characters';
      if (state.orgName.trim().length > 100) return 'organization name too long';
      return null;
    case 'email':
      if (!EMAIL_RE.test(state.email)) return 'enter a valid email address';
      return null;
    case 'password':
      if (state.password.length < 8) return 'password must be at least 8 characters';
      return null;
    case 'confirmPassword':
      if (state.confirmPassword !== state.password) return 'passwords do not match';
      return null;
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'FIELD': {
      const next = { ...state, [action.field]: action.value };
      const errors = { ...state.errors };
      delete errors[action.field];
      return { ...next, errors };
    }
    case 'TOS':
      return { ...state, tosAgreed: action.value };
    case 'BLUR': {
      const err = validateField(state, action.field);
      const errors = { ...state.errors };
      if (err) errors[action.field] = err;
      else delete errors[action.field];
      return { ...state, errors };
    }
    case 'SUBMIT':
      return { ...state, isLoading: true, errors: {} };
    case 'ERROR': {
      const errors = { ...state.errors, [action.field]: action.message };
      return { ...state, isLoading: false, errors };
    }
  }
}

interface SignupResponse {
  orgId: number;
  userId: number;
  email: string;
  orgName: string;
  role: 'owner';
  sessionToken: string;
  expiresAt: number;
}

/**
 * Route: `/signup`. Creates an org + first owner user via `POST /v1/auth/signup`,
 * stores the returned session token, then navigates to `/dashboard`. No API key
 * is issued here — the user mints their first key from the Keys page after
 * landing on the dashboard.
 */
export function SignupPage() {
  const [state, dispatch] = useReducer(reducer, initial);
  const { signupComplete } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = getReturnTo(location.search, '/dashboard/certs');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const fields: Field[] = ['orgName', 'email', 'password', 'confirmPassword'];
    const errs: Record<string, string> = {};
    for (const f of fields) {
      const err = validateField(state, f);
      if (err) errs[f] = err;
    }
    if (Object.keys(errs).length > 0) {
      for (const [f, m] of Object.entries(errs)) dispatch({ type: 'ERROR', field: f, message: m });
      return;
    }
    if (!state.tosAgreed) { dispatch({ type: 'ERROR', field: 'form', message: 'Agree to the terms before creating an account.' }); return; }
    dispatch({ type: 'SUBMIT' });
    try {
      const res = await apiFetch<SignupResponse>('/v1/auth/signup', {
        method: 'POST',
        body: { orgName: state.orgName.trim(), email: state.email, password: state.password, tosAgreed: state.tosAgreed },
        skipAuth: true,
      });
      const user: User = {
        id: res.userId,
        email: res.email,
        orgId: res.orgId,
        orgName: res.orgName,
        role: 'owner',
        schemas: [],
      };
      signupComplete(user, res.sessionToken);
      navigate(returnTo, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'signup failed';
      const field = /email/i.test(msg) ? 'email' : 'form';
      dispatch({ type: 'ERROR', field, message: msg });
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <p className="text-muted" style={{ marginBottom: 16 }}>{returnTo.startsWith('/docs') ? 'Create an account to read and download the standards.' : 'create your organization account'}</p>

      {state.errors['form'] && <Feedback type="error" message={state.errors['form']} />}

      <div className="form-row">
        <label className="label" htmlFor="signup-orgName">organization name</label>
        <input
          type="text"
          autoComplete="organization"
          id="signup-orgName"
          value={state.orgName}
          onChange={(e) => dispatch({ type: 'FIELD', field: 'orgName', value: e.target.value })}
          onBlur={() => dispatch({ type: 'BLUR', field: 'orgName' })}
          className={`form-input ${state.errors['orgName'] ? 'error' : ''}`}
        />
        {state.errors['orgName'] && <p className="form-error">{state.errors['orgName']}</p>}
      </div>

      <div className="form-row">
        <label className="label" htmlFor="signup-email">email</label>
        <input
          type="email"
          autoComplete="email"
          id="signup-email"
          value={state.email}
          onChange={(e) => dispatch({ type: 'FIELD', field: 'email', value: e.target.value })}
          onBlur={() => dispatch({ type: 'BLUR', field: 'email' })}
          className={`form-input ${state.errors['email'] ? 'error' : ''}`}
        />
        {state.errors['email'] && <p className="form-error">{state.errors['email']}</p>}
      </div>

      <div className="form-row">
        <label className="label" htmlFor="signup-password">password <span className="text-muted">(min 8 characters)</span></label>
        <PasswordInput
          autoComplete="new-password"
          id="signup-password"
          value={state.password}
          onChange={(e) => dispatch({ type: 'FIELD', field: 'password', value: e.target.value })}
          onBlur={() => dispatch({ type: 'BLUR', field: 'password' })}
          className={`form-input ${state.errors['password'] ? 'error' : ''}`}
        />
        <p className="form-hint">{state.password.length} / 8 characters</p>
        {state.errors['password'] && <p className="form-error">{state.errors['password']}</p>}
      </div>

      <div className="form-row">
        <label className="label" htmlFor="signup-confirmPassword">confirm password</label>
        <PasswordInput
          autoComplete="new-password"
          id="signup-confirmPassword"
          value={state.confirmPassword}
          onChange={(e) => dispatch({ type: 'FIELD', field: 'confirmPassword', value: e.target.value })}
          onBlur={() => dispatch({ type: 'BLUR', field: 'confirmPassword' })}
          className={`form-input ${state.errors['confirmPassword'] ? 'error' : ''}`}
        />
        {state.errors['confirmPassword'] && <p className="form-error">{state.errors['confirmPassword']}</p>}
      </div>

      <div className="form-row">
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={state.tosAgreed}
            onChange={(e) => dispatch({ type: 'TOS', value: e.target.checked })}
            style={{ marginTop: 4 }}
          />
          <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>
            {' '}and acknowledge that the AstdZ™ standards are proprietary intellectual
            property. I understand my use is subject to certification terms and audit rights
            as described in the Terms of Service.
          </span>
        </label>
      </div>

      <button
        type="submit"
        className="btn btn-primary"
        disabled={state.isLoading || !state.tosAgreed}
        style={!state.tosAgreed ? { opacity: 0.5 } : undefined}
      >
        {state.isLoading ? 'creating account...' : 'create account'}
      </button>

      <p className="text-muted" style={{ marginTop: 24 }}>
        already have an account? <Link to={authLink('/login', returnTo)}>→ sign in</Link>
      </p>

      <hr className="divider" />
      <p className="text-muted" style={{ marginBottom: 8 }}>── what happens next ──────────────────────────</p>
      <ul className="text-muted" style={{ listStyle: 'none', paddingLeft: 0 }}>
        <li>· read and download the full standards with your account</li>
        <li>· request certification for any supported standard and tier</li>
        <li>· mint a scoped API key on the Keys page and copy it once</li>
        <li>· use the console or API to encode and inspect payloads</li>
      </ul>
    </form>
  );
}
